import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * AltexAuditPage — a print-optimized 2-page website audit deliverable for
 * Altex Logistics Inc. Michael can hand-deliver this to their front desk
 * in Wheaton, IL after finding the site (altexlogistics.com) has 8 visible
 * issues costing them shipper credibility.
 *
 * Route: /audit-altex
 *
 * How to use:
 *   1. Open /audit-altex?name=Michael+Guley&phone=605-679-7303&email=michael@freightpilotai.ai
 *   2. Ctrl+P → Save as PDF (or print straight to paper)
 *   3. Walk into Altex HQ (929 Childs St, Wheaton, IL 60187), hand it to
 *      the same receptionist who mentioned the site needs work.
 *
 * Query params (all optional — sensible defaults if omitted):
 *   name   — reviewer's full name
 *   phone  — reviewer's phone
 *   email  — reviewer's email
 *   role   — reviewer's title (default: "Founder")
 *
 * The @media print styles hide the sticky instruction bar so the printed
 * PDF is JUST the audit content, no chrome.
 */
export default function AltexAuditPage() {
  const [params] = useSearchParams();
  const [reviewerName, setReviewerName] = useState(
    (params.get("name") || "Mike Manene").trim()
  );
  const [reviewerPhone, setReviewerPhone] = useState(
    (params.get("phone") || "(702) 680-5540").trim()
  );
  const [reviewerEmail, setReviewerEmail] = useState(
    (params.get("email") || "contact@freightpilotai.ai").trim()
  );
  const [reviewerRole, setReviewerRole] = useState(
    (params.get("role") || "Founder, FreightPilotAI").trim()
  );

  useEffect(() => {
    document.title = "Altex Logistics — Website Review (FreightPilotAI)";
  }, []);

  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-neutral-100 text-black">
      {/* Print-only page styling */}
      <style>{`
        @page { size: Letter portrait; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; }
          .audit-sheet { box-shadow: none !important; margin: 0 !important; page-break-after: always; }
          .audit-sheet:last-child { page-break-after: auto; }
        }
      `}</style>

      {/* Instruction bar — screen only */}
      <div className="no-print bg-neutral-900 text-white text-sm py-3 px-6 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <b>Ready to print.</b>{" "}
          Press <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Ctrl+P</kbd> (Windows) or{" "}
          <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Cmd+P</kbd> (Mac). Choose{" "}
          <b>"Save as PDF"</b> for a digital copy or send to your printer for hand-delivery.
        </div>
        <button
          onClick={() => window.print()}
          className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold rounded-md px-4 py-2 text-sm shrink-0"
          data-testid="audit-print-btn"
        >
          Print now
        </button>
      </div>

      {/* Screen-only helper: input fields so user can customize on-page (no URL editing needed) */}
      <div className="no-print max-w-3xl mx-auto mt-6 mb-4 p-5 rounded-lg border-2 border-[#FF5A1F] bg-white shadow-lg">
        <div className="font-bold text-neutral-900 mb-1 text-base">
          📝 Fill in your info below — then press "Print now" (top-right).
        </div>
        <div className="text-xs text-neutral-600 mb-4">
          The audit updates in real-time. Nothing is saved — just personalizes what prints. You do NOT need to edit any URL.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Your full name
            </span>
            <input
              type="text"
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              placeholder="e.g., Michael Guley"
              data-testid="audit-input-name"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Your title
            </span>
            <input
              type="text"
              value={reviewerRole}
              onChange={(e) => setReviewerRole(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              placeholder="e.g., Founder, FreightPilotAI"
              data-testid="audit-input-role"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Your phone
            </span>
            <input
              type="tel"
              value={reviewerPhone}
              onChange={(e) => setReviewerPhone(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              placeholder="e.g., (312) 555-1234"
              data-testid="audit-input-phone"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Your email
            </span>
            <input
              type="email"
              value={reviewerEmail}
              onChange={(e) => setReviewerEmail(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              placeholder="e.g., you@freightpilotai.ai"
              data-testid="audit-input-email"
            />
          </label>
        </div>
        <div className="mt-4 pt-3 border-t border-neutral-200 text-xs text-neutral-600">
          ✅ When it looks right, click <b>Print now</b> at the top of the page (or press <kbd className="px-1.5 py-0.5 bg-neutral-200 rounded text-[10px]">Ctrl+P</kbd>) and choose <b>"Save as PDF"</b>.
        </div>
      </div>

      {/* ═══════════════ PAGE 1 ═══════════════ */}
      <div
        className="audit-sheet mx-auto my-6 bg-white shadow-lg"
        style={{ width: "8.5in", minHeight: "11in", padding: "0.65in 0.7in" }}
        data-testid="audit-page-1"
      >
        {/* Brand strip */}
        <div className="flex items-center justify-between border-b-2 border-[#FF5A1F] pb-4 mb-6">
          <div className="flex items-baseline gap-1">
            <span className="font-black text-2xl tracking-tight">Freight</span>
            <span className="font-black text-2xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-2xl tracking-tight">AI</span>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">
              Prepared for
            </div>
            <div className="text-sm font-bold">Altex Logistics Inc.</div>
          </div>
        </div>

        {/* Report title */}
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
            Independent Website Review
          </div>
          <h1 className="text-[42px] leading-[1.02] font-black tracking-tight mb-4">
            8 issues on <span className="text-[#FF5A1F]">altexlogistics.com</span> that are quietly costing you business.
          </h1>
          <div className="flex items-center gap-6 text-[12px] text-neutral-600">
            <div>
              <span className="font-bold text-neutral-900">Reviewed:</span> altexlogistics.com
            </div>
            <div>
              <span className="font-bold text-neutral-900">Date:</span> {today}
            </div>
            <div>
              <span className="font-bold text-neutral-900">Reviewer:</span> {reviewerName}
            </div>
          </div>
        </div>

        {/* Executive summary */}
        <div className="rounded-lg border-l-4 border-[#FF5A1F] bg-[#FFF8F3] p-5 mb-8">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
            Summary
          </div>
          <p className="text-[14px] leading-relaxed text-neutral-800">
            Altex Logistics has strong fundamentals — <b>100+ trucks, 12+ years in business, active
            interstate authority (USDOT 2399662)</b>, and a documented growth story. However, the
            current website has <b>8 visible issues</b> that any shipper or CDL-A applicant can see
            today. These issues are silently reducing inbound quote requests and driver
            applications. None are opinions — they are live errors as of {today}.
          </p>
        </div>

        {/* Section header */}
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold mb-4">
          What's broken · in order of business impact
        </div>

        {/* Issues 1–4 */}
        <div className="space-y-5">
          <Issue
            n="1"
            impact="HIGH"
            title="Contact information is still placeholder text"
            issue={
              <>
                Phone displays as <code className="font-mono bg-neutral-100 px-1 rounded">123-123-1234</code>{" "}
                and email as <code className="font-mono bg-neutral-100 px-1 rounded">info@altex.com</code>{" "}
                (wrong domain — should be @altexlogistics.com).
              </>
            }
            cost="Every shipper or driver who tries to reach out gets nothing. Direct conversion loss."
            fix="30 minutes"
          />
          <Issue
            n="2"
            impact="HIGH"
            title='"By the Numbers" section shows 0+ everywhere'
            issue='Homepage stats section displays "0+ Trucks, 0+ Owner Operators, 0+ Customers Served, 0+ Years in Business."'
            cost="Visitors read '0' and assume the company is inactive or brand new. Directly contradicts the actual 100+ trucks and 12+ years in business."
            fix="15 minutes"
          />
          <Issue
            n="3"
            impact="HIGH"
            title="Driver pay info contradicts itself in two places"
            issue={
              <>
                One section says <b>"$2,500–$3,200/week"</b> for company drivers and{" "}
                <b>"$8,000–$10,000/week"</b> for owner-ops. Another section says{" "}
                <b>"$2,000–$2,500/week"</b> for both.
              </>
            }
            cost="CDL-A drivers see conflicting pay claims and move on to a competitor. Direct recruiting cost."
            fix="15 minutes"
          />
          <Issue
            n="4"
            impact="MEDIUM"
            title="Services section repeats 3 times in a row"
            issue="The same 7 services (Reefer, Flatbed, Conestoga, FTL, LTL, Truck Shop, Dry Van) are listed three consecutive times — a broken carousel/slider stuck in a static loop."
            cost="Looks amateur to shippers evaluating carriers. Signals the site isn't maintained."
            fix="~1 hour"
          />
        </div>

        {/* Footer of page 1 */}
        <div className="mt-8 pt-4 border-t border-neutral-300 flex items-center justify-between text-[11px] text-neutral-500">
          <div>
            <b className="text-neutral-900">FreightPilotAI</b> · Independent website review for Altex Logistics
          </div>
          <div>Page 1 of 2 · continued →</div>
        </div>
      </div>

      {/* ═══════════════ PAGE 2 ═══════════════ */}
      <div
        className="audit-sheet mx-auto my-6 bg-white shadow-lg"
        style={{ width: "8.5in", minHeight: "11in", padding: "0.65in 0.7in" }}
        data-testid="audit-page-2"
      >
        {/* Header strip */}
        <div className="flex items-center justify-between border-b-2 border-[#FF5A1F] pb-4 mb-6">
          <div className="flex items-baseline gap-1">
            <span className="font-black text-lg tracking-tight">Freight</span>
            <span className="font-black text-lg tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-lg tracking-tight">AI</span>
            <span className="ml-3 text-[11px] text-neutral-500 uppercase tracking-widest">
              Altex Website Review — continued
            </span>
          </div>
          <div className="text-[11px] text-neutral-500">Page 2 of 2</div>
        </div>

        {/* Issues 5–8 */}
        <div className="space-y-5 mb-8">
          <Issue
            n="5"
            impact="MEDIUM"
            title="Team section also repeats 3 times"
            issue="The same 8 team members (D'arcy Young, David Carpenter, Colin Moore, Bryan Botterman, Nesta Murphy, Luke Logan, Chad Cook, Ty Thaggard) appear three times each. Same broken carousel pattern as #4."
            cost="Erodes trust — visitors notice the pattern immediately and question the company's attention to detail."
            fix="~1 hour"
          />
          <Issue
            n="6"
            impact="MEDIUM"
            title="Unrelated products listed at the footer"
            issue={
              <>
                The bottom of the homepage lists <b>"apparel, eco-friendly containers, handbags &amp; accessories,
                emergency tools, masks, hats"</b> — leftover Webflow template content unrelated to trucking.
              </>
            }
            cost="Confuses visitors about what Altex actually does. Damages perceived professionalism."
            fix="15 minutes"
          />
          <Issue
            n="7"
            impact="LOW"
            title="Original Webflow template placeholders never replaced"
            issue="Site is built on Webflow (identifiable from CDN asset URLs). The original template's placeholder content remains in multiple places, suggesting the agency that built it either shipped it early or was replaced before completion."
            cost="Ongoing changes will keep being one-off band-aids without a proper maintenance plan. Slow drag on brand quality."
            fix="Ongoing / retainer"
          />
          <Issue
            n="8"
            impact="MEDIUM"
            title="No visible analytics or conversion tracking"
            issue="Public source code shows no signals of Google Analytics 4, Meta Pixel, or Google Ads conversion tracking. This means Altex has no data on which pages drive contact form submissions or driver applications."
            cost="Marketing decisions are being made blind. Can't optimize what you can't measure."
            fix="~2 hours"
          />
        </div>

        {/* Recommended paths */}
        <div className="rounded-xl border-2 border-neutral-900 p-5 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
            Recommended paths forward
          </div>
          <div className="space-y-3">
            <PathRow
              label="Option A — Emergency Cleanup"
              price="$1,500"
              detail="Fix items 1, 2, 3, 4, 5, 6. One week. Live and clean. This alone gets you back to zero embarrassment."
            />
            <PathRow
              label="Option B — Cleanup + Modernization"
              price="$3,500"
              detail="All of Option A, plus: refreshed design, working driver careers form, Google Analytics 4 + conversion tracking, and full mobile optimization. Two weeks."
            />
            <PathRow
              label="Option C — Full Rebuild"
              price="$8,000+"
              detail="New site from scratch. Custom driver careers portal, shipper quote form, SEO-optimized copy, and 30 days of post-launch support. 4–6 weeks."
            />
          </div>
          <div className="mt-3 text-[11px] text-neutral-600 leading-snug">
            All options include: direct communication with me (no account managers), one round of revisions included,
            and full handoff documentation so your team can maintain the site after delivery.
          </div>
        </div>

        {/* About the reviewer */}
        <div className="border-t-2 border-neutral-900 pt-5 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold mb-2">
            About the reviewer
          </div>
          <p className="text-[13px] leading-relaxed text-neutral-800 mb-3">
            I'm {reviewerName}, {reviewerRole}. I build software and websites specifically for trucking
            operations. Before recommending anything, I do a real teardown of what's on-screen today — because
            most agencies pitch redesigns before understanding what's broken. Happy to walk through any of these
            findings on a 15-minute call. No commitment.
          </p>
        </div>

        {/* Contact block */}
        <div className="rounded-lg bg-neutral-900 text-white p-5 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
            Reach me directly
          </div>
          <div className="grid grid-cols-2 gap-4 text-[13px]">
            <div>
              <div className="text-neutral-400 text-[10px] uppercase tracking-widest mb-1">Phone</div>
              <div className="font-bold text-white">{reviewerPhone}</div>
            </div>
            <div>
              <div className="text-neutral-400 text-[10px] uppercase tracking-widest mb-1">Email</div>
              <div className="font-bold text-white break-all">{reviewerEmail}</div>
            </div>
            <div>
              <div className="text-neutral-400 text-[10px] uppercase tracking-widest mb-1">Website</div>
              <div className="font-bold text-white">freightpilotai.ai</div>
            </div>
            <div>
              <div className="text-neutral-400 text-[10px] uppercase tracking-widest mb-1">Location</div>
              <div className="font-bold text-white">Wheaton, IL</div>
            </div>
          </div>
        </div>

        {/* Bottom footer */}
        <div className="pt-3 border-t border-neutral-300 flex items-center justify-between text-[10px] text-neutral-500">
          <div>Prepared exclusively for Altex Logistics Inc. · Not for redistribution.</div>
          <div>{today}</div>
        </div>
      </div>
    </div>
  );
}

function Issue({ n, impact, title, issue, cost, fix }) {
  const impactColor = {
    HIGH: "bg-red-100 text-red-900 border-red-300",
    MEDIUM: "bg-amber-100 text-amber-900 border-amber-300",
    LOW: "bg-neutral-100 text-neutral-700 border-neutral-300",
  }[impact];

  return (
    <div className="grid grid-cols-[42px_1fr] gap-4">
      <div className="flex flex-col items-center">
        <div className="w-9 h-9 rounded-full bg-[#FF5A1F] text-black font-black text-lg flex items-center justify-center">
          {n}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded font-bold border ${impactColor}`}>
            {impact} impact
          </span>
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">
            Fix: {fix}
          </span>
        </div>
        <h3 className="text-[16px] font-bold leading-tight text-neutral-900 mb-1.5">
          {title}
        </h3>
        <p className="text-[12.5px] text-neutral-700 leading-snug mb-1.5">
          <b className="text-neutral-900">What's happening:</b> {issue}
        </p>
        <p className="text-[12.5px] text-neutral-700 leading-snug">
          <b className="text-neutral-900">Business cost:</b> {cost}
        </p>
      </div>
    </div>
  );
}

function PathRow({ label, price, detail }) {
  return (
    <div className="flex gap-4 items-start pb-3 border-b border-neutral-200 last:border-b-0 last:pb-0">
      <div className="w-[95px] shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Price</div>
        <div className="text-[18px] font-black text-[#FF5A1F]">{price}</div>
      </div>
      <div>
        <div className="text-[13px] font-bold text-neutral-900 mb-1">{label}</div>
        <div className="text-[12px] text-neutral-700 leading-snug">{detail}</div>
      </div>
    </div>
  );
}
